import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NewJobDialog } from "@/modules/interventions/NewJobDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton, StatusBadge } from "@/components/common";
import { VehicleFields, EMPTY_VEHICLE, vehiclePayload } from "@/components/pro/VehicleForm";
import { SlotPicker, fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { fmtDateTime } from "@/lib/format";
import { useFetch } from "@/lib/useFetch";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg, fileUrl } from "@/lib/api";
import { t } from "@/i18n";
import { SecureLinkButton } from "@/modules/secure_links/SecureLinkButton";

const NEW = "__new";

export function PhoneBookingDialog({ open, onOpenChange, tz, onDone }) {
  const customers = useFetch("/customers");
  const services = useFetch("/garage/services");
  const [f, setF] = useState({ customer_id: "", vehicle_id: "", service_id: "", description: "" });
  const [nc, setNc] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [nv, setNv] = useState(EMPTY_VEHICLE);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const vehicles = useFetch("/vehicles", { customer_id: f.customer_id && f.customer_id !== NEW ? f.customer_id : "none" });
  const load = useCallback((day) => api.get("/booking/slots", { params: { service_id: f.service_id, day } }).then((r) => r.data), [f.service_id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!slot) return setError(t("sched.pickTime"));
    setBusy(true);
    setError("");
    const newCust = f.customer_id === NEW;
    const newVeh = newCust || f.vehicle_id === NEW;
    try {
      await api.post("/appointments", {
        service_id: f.service_id, start: slot.start, mechanic_id: slot.mechanic_id, description: f.description,
        customer_id: newCust ? null : f.customer_id, new_customer: newCust ? { ...nc, email: nc.email || null } : null,
        vehicle_id: newVeh ? null : f.vehicle_id, new_vehicle: newVeh ? vehiclePayload(nv) : null,
      });
      toast.success(t("sched.booked"));
      setSlot(null);
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const vlist = f.customer_id && f.customer_id !== NEW ? vehicles.data || [] : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="phone-booking-dialog">
        <DialogHeader><DialogTitle>{t("sched.phoneBooking")}</DialogTitle><DialogDescription>{t("sched.phoneHint")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <FormError message={error} />
          <Field label={t("sched.customer")}>
            <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
              <SelectTrigger data-testid="pb-customer-select"><SelectValue placeholder={t("sched.chooseCustomer")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW}>{t("sched.newCustomer")}</SelectItem>
                {(customers.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`.trim()} {c.phone ? `· ${c.phone}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {f.customer_id === NEW && (
            <div className="grid gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-4">
              {["first_name", "last_name", "phone", "email"].map((k) => (
                <Field key={k} label={t(`sched.nc.${k}`)}><Input required={k === "first_name"} value={nc[k]} onChange={(e) => setNc({ ...nc, [k]: e.target.value })} data-testid={`pb-new-${k}`} /></Field>
              ))}
            </div>
          )}
          {f.customer_id && f.customer_id !== NEW && (
            <Field label={t("sched.vehicle")}>
              <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
                <SelectTrigger data-testid="pb-vehicle-select"><SelectValue placeholder={t("sched.chooseVehicle")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NEW}>{t("sched.newVehicle")}</SelectItem>
                  {vlist.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.make} {v.model}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {(f.customer_id === NEW || f.vehicle_id === NEW) && <div className="rounded-lg border border-slate-200 p-4"><VehicleFields v={nv} setV={setNv} /></div>}
          <Field label={t("sched.service")}>
            <Select value={f.service_id} onValueChange={(v) => { setSlot(null); setF({ ...f, service_id: v }); }}>
              <SelectTrigger data-testid="pb-service-select"><SelectValue placeholder={t("sched.chooseService")} /></SelectTrigger>
              <SelectContent>{(services.data || []).filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.duration_min} min</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.service_id && <SlotPicker load={load} tz={tz} value={slot} onChange={setSlot} showMechanics testPrefix="pb-slot" />}
          <Field label={t("sched.notes")}><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pb-description" /></Field>
          <div className="gf-modal-footer">
          <SubmitButton busy={busy} className="w-full" disabled={!f.service_id || !f.customer_id} data-testid="pb-submit">{t("sched.confirmBooking")}</SubmitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PartsPanel({ appt, act, busy }) {
  const { hasFeature } = useGarage();
  const catalogue = useFetch(hasFeature("inventory") ? "/inventory/parts" : null);
  const [lines, setLines] = useState([]);
  const [pick, setPick] = useState("");
  const [note, setNote] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [reason, setReason] = useState("");
  const parts = catalogue.data || [];
  const add = () => { if (pick && !lines.find((l) => l.part_id === pick)) setLines([...lines, { part_id: pick, qty: 1 }]); setPick(""); };
  return (
    <div className="mt-6 space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="parts-panel">
      <p className="font-semibold text-amber-950">{t("b9.parts.title")}</p>
      {hasFeature("inventory") && (
        <div className="space-y-2">
          <p className="text-xs text-slate-600">{t("b9.parts.link")}</p>
          <div className="flex gap-2">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="flex-1 bg-white" data-testid="parts-pick"><SelectValue placeholder={t("b9.parts.pickPart")} /></SelectTrigger>
              <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name} ({t("b9.parts.onHand", { n: p.qty_on_hand ?? 0 })})</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" variant="outline" onClick={add} disabled={!pick} data-testid="parts-add">+</Button>
          </div>
          {lines.map((l, i) => {
            const p = parts.find((x) => x.id === l.part_id) || {};
            return (
              <div key={l.part_id} className="flex items-center gap-2 text-sm" data-testid={`parts-line-${i}`}>
                <span className="flex-1 truncate">{p.sku} · {p.name}</span>
                <Input type="number" min={0.01} step="any" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} className="w-20 bg-white" aria-label={t("b9.parts.qty")} />
                <Button type="button" variant="ghost" size="sm" onClick={() => setLines(lines.filter((_, j) => j !== i))}>×</Button>
              </div>
            );
          })}
        </div>
      )}
      <Field label={t("b9.parts.note")}><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" data-testid="parts-note" /></Field>
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => act("parts-confirm", { parts: lines.map((l) => ({ part_id: l.part_id, qty: Number(l.qty) })), note }, t("b9.parts.confirmed"))} data-testid="parts-confirm-button">{t("b9.parts.confirm")}</Button>
        <Button variant="outline" className="text-red-600" onClick={() => setUnavailable(!unavailable)} data-testid="parts-unavailable-button">{t("b9.parts.unavailable")}</Button>
      </div>
      {unavailable && (
        <div className="space-y-2">
          <Field label={t("b9.parts.reason")}><Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="bg-white" data-testid="parts-unavailable-reason" /></Field>
          <Button variant="destructive" disabled={busy} onClick={() => act("parts-unavailable", { reason }, t("b9.parts.marked"))} data-testid="parts-unavailable-submit">{t("b9.parts.unavailable")}</Button>
        </div>
      )}
    </div>
  );
}

export function AppointmentSheet({ appt, tz, onClose, onChanged, readOnly }) {
  const [mode, setMode] = useState(null);
  const [slot, setSlot] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [receive, setReceive] = useState(false);
  const navigate = useNavigate();
  const load = useCallback((day) => api.get("/booking/slots", { params: { service_id: appt?.service_id, day } }).then((r) => r.data), [appt?.service_id]);
  if (!appt) return null;
  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/appointments/${appt.id}/${path}`, body || {});
      toast.success(msg);
      setMode(null);
      setSlot(null);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const active = ["pending", "confirmed", "awaiting_parts"].includes(appt.status);
  return (
    <Sheet open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="appointment-sheet">
        <SheetHeader>
          <SheetTitle>{appt.service_name}</SheetTitle>
          <SheetDescription data-testid="appointment-when">{fmtDayLong(appt.start, tz)} · {appt.open_ended ? t("b11.dropoffAt", { time: fmtTime(appt.start, tz) }) : `${fmtTime(appt.start, tz)}–${fmtTime(appt.end, tz)}`}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex flex-wrap gap-2">
          <StatusBadge status={appt.status} testId="appointment-status" />
          <StatusBadge status={appt.slot_type} />
          {appt.proposal && <StatusBadge status="proposed" testId="appointment-proposal-badge" />}
        </div>
        <dl className="mt-5 space-y-2 text-sm">
          {[["sched.customer", `${appt.customer_name}${appt.customer_phone ? ` · ${appt.customer_phone}` : ""}`], ["sched.vehicle", appt.vehicle],
            ["sched.mechanic", appt.mechanic_name], ["sched.source", t(`sched.sources.${appt.source}`)],
            ...(appt.own_parts != null ? [["b9.parts.own", appt.own_parts ? t("common.yes") : t("common.no")]] : []),
            ...(appt.customer_parts_confirmation ? [["b10.cparts.staff", t("b10.cparts.by", { who: appt.customer_parts_confirmation.by_email, when: fmtDateTime(appt.customer_parts_confirmation.at, tz) })]] : [])].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4"><dt className="text-slate-500">{t(k)}</dt><dd className="text-right font-medium">{v || "—"}</dd></div>
          ))}
        </dl>
        {appt.proposal && <p className="mt-4 rounded-lg bg-sky-50 p-3 text-sm text-sky-900" data-testid="appointment-proposal">{t("sched.proposalPending", { time: `${fmtDayLong(appt.proposal.start, tz)} ${fmtTime(appt.proposal.start, tz)}` })}</p>}
        {appt.proposal && !readOnly && <div className="mt-2"><SecureLinkButton kind="appointment" refId={appt.id} /></div>}
        {appt.parts_links?.length > 0 && <div className="mt-4 text-sm" data-testid="appointment-parts-links"><p className="font-semibold">{t("b9.parts.linked")}</p>{appt.parts_links.map((l) => <p key={l.part_id} className="text-slate-700">{l.qty} × {l.sku} · {l.name}</p>)}</div>}
        {appt.parts_note && <p className="mt-2 text-xs text-slate-600">{appt.parts_note}</p>}
        {appt.status === "awaiting_parts" && !readOnly && <PartsPanel appt={appt} act={act} busy={busy} />}
        <p className={`mt-4 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm ${appt.description ? "" : "italic text-slate-500"}`} data-testid="appointment-description">{appt.description || t("b11.noDescription")}</p>
        {appt.own_parts_details && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-amber-50 p-3 text-sm" data-testid="appointment-parts-details"><span className="font-semibold">{t("b11.partsDetails")}:</span> {appt.own_parts_details}</p>}
        {appt.photos?.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="appointment-photos">
            {appt.photos.map((p) => <a key={p.id} href={fileUrl(p.url)} target="_blank" rel="noreferrer"><img src={fileUrl(p.url)} alt="" className="h-24 w-full rounded-lg object-cover" /></a>)}
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          {appt.job_id && <Button asChild variant="outline" data-testid="appt-open-job"><Link to={`/pro/jobs/${appt.job_id}`}>{t("jobs.openJob")}</Link></Button>}
          {!appt.job_id && !readOnly && ["pending", "confirmed", "checked_in"].includes(appt.status) && <Button onClick={() => setReceive(true)} data-testid="appt-receive-vehicle">{t("jobs.receiveVehicle")}</Button>}
        </div>
        {receive && <NewJobDialog open={receive} onOpenChange={setReceive} appointment={appt} onCreated={(j) => navigate(`/pro/jobs/${j.id}`)} />}
        {active && !readOnly && (
          <div className="mt-6 space-y-3">
            <div className="flex flex-wrap gap-2">
              {appt.status === "pending" && <Button disabled={busy} onClick={() => act("confirm", null, t("sched.confirmed"))} data-testid="appt-confirm-button">{t("sched.confirm")}</Button>}
              {appt.status === "pending" && <Button variant="outline" onClick={() => setMode("decline")} data-testid="appt-decline-button">{t("sched.decline")}</Button>}
              {appt.status !== "awaiting_parts" && <Button variant="outline" onClick={() => setMode("move")} data-testid="appt-reschedule-button">{t("sched.reschedule")}</Button>}
              <Button variant="outline" className="text-red-600" onClick={() => setMode("cancel")} data-testid="appt-cancel-button">{t("sched.cancel")}</Button>
            </div>
            {(mode === "decline" || mode === "cancel") && (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <Field label={t("sched.reasonForCustomer")}><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="appt-reason-input" /></Field>
                <Button variant="destructive" disabled={busy} onClick={() => act(mode, { reason }, mode === "decline" ? t("sched.declined") : t("sched.cancelled"))} data-testid="appt-reason-submit">
                  {mode === "decline" ? t("sched.confirmDecline") : t("sched.confirmCancel")}
                </Button>
              </div>
            )}
            {mode === "move" && (
              <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                <SlotPicker load={load} tz={tz} value={slot} onChange={setSlot} showMechanics testPrefix="move-slot" />
                <div className="flex flex-wrap gap-2">
                  <Button disabled={!slot || busy} onClick={() => act("reschedule", { start: slot.start, mechanic_id: slot.mechanic_id }, t("sched.moved"))} data-testid="appt-move-now">{t("sched.moveNow")}</Button>
                  <Button variant="outline" disabled={!slot || busy} onClick={() => act("propose", { start: slot.start, mechanic_id: slot.mechanic_id }, t("sched.proposed"))} data-testid="appt-propose">{t("sched.propose")}</Button>
                </div>
                <p className="text-xs text-slate-500">{t("sched.moveHint")}</p>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
